import { useState, useEffect } from 'react';
import { useOutletContext, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Calendar, Users, Star, Brain,
  Pencil, X, Save, Check, Plus, Minus
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ratingOptions = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'average', label: 'Average' },
  { value: 'below_avg', label: 'Below Avg' },
  { value: 'poor', label: 'Poor' },
];

const flagOptions = [
  { value: 'no_issue', label: 'No Issue' },
  { value: 'performance_drop', label: 'Performance Drop' },
  { value: 'needs_development', label: 'Needs Development' },
  { value: 'inconsistency', label: 'Inconsistency' },
  { value: 'low_effort', label: 'Low Effort' },
  { value: 'injury_concern', label: 'Injury Concern' },
];

const contributionOptions = [
  'goal', 'assist', 'defensive_impact', 'effort', 'leadership',
];

const teamPerfOptions = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'average', label: 'Average' },
  { value: 'poor', label: 'Poor' },
];

const teamIssueOptions = [
  'defensive_organization', 'passing_accuracy', 'decision_making',
  'physical_intensity', 'communication', 'set_pieces',
];

const teamStrengthOptions = [
  'team_spirit', 'offensive_flow', 'defensive_solidity',
  'ball_possession', 'counter_attack', 'set_pieces',
];

const MatchReportDetail = () => {
  const { club } = useOutletContext();
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [allPlayers, setAllPlayers] = useState([]);

  useEffect(() => {
    if (club?.id && reportId) fetchReport();
  }, [club, reportId]);

  const fetchReport = async () => {
    const token = localStorage.getItem('club_token');
    try {
      const res = await axios.get(`${API}/clubs/matches/report/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const startEditing = async () => {
    const token = localStorage.getItem('club_token');
    try {
      const res = await axios.get(`${API}/clubs/matches/report/${reportId}/edit-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { report: rpt, all_players } = res.data;
      setEditData({
        opponent: rpt.opponent,
        our_score: rpt.our_score,
        opponent_score: rpt.opponent_score,
        match_date: rpt.match_date?.split('T')[0] || '',
        team_performance: rpt.team_performance || 'average',
        team_issues: rpt.team_issues || [],
        team_strong_points: rpt.team_strong_points || [],
        player_performances: all_players.map(p => ({
          ...p,
          included: p.evaluated || false,
        })),
      });
      setAllPlayers(all_players);
      setEditing(true);
    } catch (err) {
      toast.error('Failed to load edit data');
      console.error(err);
    }
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditData({});
  };

  const togglePlayer = (index) => {
    setEditData(prev => {
      const players = [...prev.player_performances];
      players[index] = {
        ...players[index],
        included: !players[index].included,
        rating: players[index].rating || 'average',
      };
      return { ...prev, player_performances: players };
    });
  };

  const updatePlayerField = (index, field, value) => {
    setEditData(prev => {
      const players = [...prev.player_performances];
      players[index] = { ...players[index], [field]: value };
      return { ...prev, player_performances: players };
    });
  };

  const toggleContribution = (index, contrib) => {
    setEditData(prev => {
      const players = [...prev.player_performances];
      const p = { ...players[index] };
      const contribs = [...(p.contributions || [])];
      const idx = contribs.indexOf(contrib);
      if (idx >= 0) contribs.splice(idx, 1);
      else contribs.push(contrib);
      p.contributions = contribs;
      players[index] = p;
      return { ...prev, player_performances: players };
    });
  };

  const toggleTeamIssue = (issue) => {
    setEditData(prev => {
      const list = [...(prev.team_issues || [])];
      const idx = list.indexOf(issue);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(issue);
      return { ...prev, team_issues: list };
    });
  };

  const toggleTeamStrength = (str) => {
    setEditData(prev => {
      const list = [...(prev.team_strong_points || [])];
      const idx = list.indexOf(str);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(str);
      return { ...prev, team_strong_points: list };
    });
  };

  const saveChanges = async () => {
    setSaving(true);
    const token = localStorage.getItem('club_token');
    try {
      const payload = {
        ...editData,
        player_performances: editData.player_performances
          .filter(p => p.included)
          .map(({ included, evaluated, ...rest }) => rest),
      };
      const res = await axios.put(`${API}/clubs/matches/report/${reportId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(res.data);
      setEditing(false);
      toast.success('Match report updated. Scores recalculated.');
    } catch (err) {
      toast.error('Failed to save changes');
      console.error(err);
    }
    setSaving(false);
  };

  const fmt = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }); }
    catch { return d; }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!report) return (
    <div className="text-center py-20">
      <p className="text-muted-foreground">Report not found</p>
      <Link to={`/club/${club?.id}/match-history`} className="text-primary text-sm hover:underline mt-2 inline-block">Back</Link>
    </div>
  );

  const players = report.player_performances || [];
  const sorted = [...players].sort((a, b) => (b.match_score || b.rating_score || 0) - (a.match_score || a.rating_score || 0));

  return (
    <div className="space-y-5" data-testid="match-report-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link to={`/club/${club?.id}/match-history`}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm"
        >
          <ArrowLeft size={16} /> Back to Match Reports
        </Link>
        {!editing ? (
          <button onClick={startEditing}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-sm text-sm text-primary hover:bg-primary/20 transition-colors"
            data-testid="edit-report-btn"
          >
            <Pencil size={14} /> Edit Report
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={cancelEditing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 border border-border/40 rounded-sm text-sm text-muted-foreground hover:text-white transition-colors"
              data-testid="cancel-edit-btn"
            >
              <X size={14} /> Cancel
            </button>
            <button onClick={saveChanges} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-primary text-black rounded-sm text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              data-testid="save-report-btn"
            >
              {saving ? <div className="w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <Save size={14} />}
              Save & Recalculate
            </button>
          </div>
        )}
      </div>

      {/* EDIT MODE */}
      {editing ? (
        <>
          {/* Match Details Edit */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-primary/20 rounded-sm p-5"
          >
            <h3 className="text-xs uppercase tracking-widest text-primary mb-4">Match Details</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <EditField label="Opponent">
                <input type="text" value={editData.opponent || ''} onChange={e => setEditData(p => ({ ...p, opponent: e.target.value }))}
                  className="field" data-testid="edit-opponent" />
              </EditField>
              <EditField label="Our Score">
                <input type="number" min="0" value={editData.our_score ?? 0} onChange={e => setEditData(p => ({ ...p, our_score: parseInt(e.target.value) || 0 }))}
                  className="field" data-testid="edit-our-score" />
              </EditField>
              <EditField label="Opponent Score">
                <input type="number" min="0" value={editData.opponent_score ?? 0} onChange={e => setEditData(p => ({ ...p, opponent_score: parseInt(e.target.value) || 0 }))}
                  className="field" data-testid="edit-opponent-score" />
              </EditField>
              <EditField label="Match Date">
                <input type="date" value={editData.match_date || ''} onChange={e => setEditData(p => ({ ...p, match_date: e.target.value }))}
                  className="field" data-testid="edit-match-date" />
              </EditField>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mt-4">
              <EditField label="Team Performance">
                <select value={editData.team_performance || 'average'} onChange={e => setEditData(p => ({ ...p, team_performance: e.target.value }))}
                  className="field" data-testid="edit-team-performance"
                >
                  {teamPerfOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </EditField>
              <EditField label="Team Issues">
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {teamIssueOptions.map(ti => (
                    <button key={ti} type="button"
                      onClick={() => toggleTeamIssue(ti)}
                      className={`text-[9px] px-1.5 py-0.5 rounded-sm border transition-colors ${
                        editData.team_issues?.includes(ti) ? 'bg-red-500/15 text-red-400 border-red-500/30' : 'bg-zinc-800 text-zinc-500 border-border/30 hover:border-red-500/30'
                      }`}
                    >{ti.replace(/_/g, ' ')}</button>
                  ))}
                </div>
              </EditField>
              <EditField label="Team Strengths">
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {teamStrengthOptions.map(ts => (
                    <button key={ts} type="button"
                      onClick={() => toggleTeamStrength(ts)}
                      className={`text-[9px] px-1.5 py-0.5 rounded-sm border transition-colors ${
                        editData.team_strong_points?.includes(ts) ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'bg-zinc-800 text-zinc-500 border-border/30 hover:border-emerald-500/30'
                      }`}
                    >{ts.replace(/_/g, ' ')}</button>
                  ))}
                </div>
              </EditField>
            </div>
          </motion.div>

          {/* Players Edit - ALL TEAM ATHLETES */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="bg-card border border-primary/20 rounded-sm overflow-hidden"
          >
            <div className="p-4 border-b border-border/30 flex items-center justify-between">
              <h3 className="text-xs uppercase tracking-widest text-primary flex items-center gap-2">
                <Users size={13} /> Player Evaluations — All Team Athletes
              </h3>
              <span className="text-[10px] text-muted-foreground">
                {editData.player_performances?.filter(p => p.included).length || 0} of {editData.player_performances?.length || 0} included
              </span>
            </div>
            <div className="divide-y divide-border/10">
              {(editData.player_performances || []).map((p, i) => (
                <PlayerEditRow
                  key={p.athlete_id}
                  player={p}
                  index={i}
                  onToggle={() => togglePlayer(i)}
                  onChange={updatePlayerField}
                  onToggleContrib={toggleContribution}
                />
              ))}
            </div>
          </motion.div>
        </>
      ) : (
        <>
          {/* READ-ONLY VIEW */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border/40 rounded-sm p-6"
          >
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className={`flex items-center justify-center gap-4 p-4 rounded-sm border ${
                report.result === 'win' ? 'bg-emerald-500/10 border-emerald-500/20' :
                report.result === 'loss' ? 'bg-red-500/10 border-red-500/20' :
                'bg-amber-500/10 border-amber-500/20'
              }`}>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase mb-1">{report.team_name}</p>
                  <p className="font-heading text-4xl text-primary">{report.our_score}</p>
                </div>
                <span className="text-2xl text-zinc-600 font-heading">:</span>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase mb-1">{report.opponent}</p>
                  <p className="font-heading text-4xl">{report.opponent_score}</p>
                </div>
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <span className={`text-xs uppercase px-2 py-1 rounded-sm border font-bold ${
                    report.result === 'win' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' :
                    report.result === 'loss' ? 'bg-red-500/15 text-red-400 border-red-500/20' :
                    'bg-amber-500/15 text-amber-400 border-amber-500/20'
                  }`}>{report.result?.toUpperCase()}</span>
                  {report.avg_player_score > 0 && (
                    <span className="text-xs text-muted-foreground">Avg Score: <span className="text-primary font-heading">{report.avg_player_score}</span></span>
                  )}
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><Calendar size={14} /> {fmt(report.match_date)}</span>
                  <span className="flex items-center gap-1.5"><Users size={14} /> {players.length} players</span>
                  <span className="flex items-center gap-1.5"><Star size={14} /> Team: {report.team_performance}</span>
                </div>
                {report.team_issues?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {report.team_issues.map((issue, j) => (
                      <span key={j} className="text-[10px] px-1.5 py-0.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded-sm">
                        {issue.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                )}
                {report.team_strong_points?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {report.team_strong_points.map((sp, j) => (
                      <span key={j} className="text-[10px] px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-sm">
                        {sp.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* AI Summary */}
          {report.post_match_summary && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-card border border-border/40 rounded-sm p-5"
            >
              <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                <Brain size={13} className="text-primary" /> AI Post-Match Summary
              </h2>
              {typeof report.post_match_summary === 'string' ? (
                <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-line">{report.post_match_summary}</p>
              ) : (
                <AISummaryBlock summary={report.post_match_summary} />
              )}
            </motion.div>
          )}

          {/* Player Performances (read-only) */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-card border border-border/40 rounded-sm overflow-hidden"
          >
            <div className="p-4 border-b border-border/30">
              <h2 className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <Users size={13} className="text-primary" /> Player Performances ({players.length})
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="border-b border-border/30 bg-zinc-900/50">
                    {['Player', 'Score', 'Rating', 'Goals', 'Assists', 'Saves', 'Contributions', 'Flag'].map(h => (
                      <th key={h} className={`px-3 py-2.5 text-[10px] uppercase tracking-widest text-muted-foreground ${h === 'Player' || h === 'Contributions' || h === 'Flag' ? 'text-left' : 'text-center'} ${h === 'Player' ? 'px-4' : ''}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((p, i) => {
                    const sc = p.match_score || p.rating_score || 0;
                    return (
                      <tr key={i} className="border-b border-border/10 hover:bg-zinc-800/20 transition-colors">
                        <td className="px-4 py-3">
                          <Link to={`/club/${club?.id}/intelligence/player/${p.athlete_id}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                            <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-[10px] font-bold text-primary shrink-0">{p.athlete_name?.charAt(0)}</div>
                            <div>
                              <p className="text-sm font-medium">{p.athlete_name}</p>
                              {p.position_group && <p className="text-[10px] text-muted-foreground capitalize">{p.position_group}</p>}
                            </div>
                          </Link>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`font-heading text-lg ${sc >= 8 ? 'text-emerald-400' : sc >= 6 ? 'text-amber-400' : 'text-red-400'}`}>{sc > 0 ? sc.toFixed(1) : '—'}</span>
                        </td>
                        <td className="px-3 py-3 text-center text-sm text-muted-foreground capitalize">{p.rating?.replace(/_/g, ' ')}</td>
                        <td className="px-3 py-3 text-center"><span className={`font-mono text-sm ${p.goals > 0 ? 'text-primary' : 'text-zinc-600'}`}>{p.goals || '—'}</span></td>
                        <td className="px-3 py-3 text-center"><span className={`font-mono text-sm ${p.assists > 0 ? 'text-emerald-400' : 'text-zinc-600'}`}>{p.assists || '—'}</span></td>
                        <td className="px-3 py-3 text-center"><span className={`font-mono text-sm ${p.saves > 0 ? 'text-blue-400' : 'text-zinc-600'}`}>{p.saves || '—'}</span></td>
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(p.contributions || []).map((c, ci) => (
                              <span key={ci} className="text-[9px] px-1 py-0.5 bg-primary/10 text-primary border border-primary/20 rounded-sm">{c.replace(/_/g, ' ')}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          {p.flag && p.flag !== 'no_issue' && (
                            <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded-sm border ${
                              p.flag === 'performance_drop' ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                            }`}>{p.flag.replace(/_/g, ' ')}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Notes */}
          {players.some(p => p.note) && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="bg-card border border-border/40 rounded-sm p-5"
            >
              <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Player Notes</h2>
              <div className="space-y-2">
                {players.filter(p => p.note).map((p, i) => (
                  <div key={i} className="p-3 bg-zinc-900/40 rounded-sm border border-border/10">
                    <p className="text-xs text-primary font-medium mb-1">{p.athlete_name}</p>
                    <p className="text-sm text-muted-foreground">{p.note}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
};


/* ============ Player Edit Row ============ */
const PlayerEditRow = ({ player: p, index, onToggle, onChange, onToggleContrib }) => {
  const isGK = p.position_group === 'goalkeeper';
  const isDef = p.position_group === 'defense';

  return (
    <div className={`px-4 py-3 ${p.included ? 'bg-zinc-900/20' : 'bg-zinc-900/5 opacity-60'}`}>
      <div className="flex items-center gap-3 mb-2">
        {/* Include toggle */}
        <button
          onClick={onToggle}
          className={`w-6 h-6 rounded-sm border flex items-center justify-center transition-colors shrink-0 ${
            p.included ? 'bg-primary/20 border-primary/40 text-primary' : 'bg-zinc-800 border-border/40 text-zinc-600'
          }`}
          data-testid={`toggle-player-${index}`}
        >
          {p.included ? <Check size={14} /> : <Plus size={14} />}
        </button>

        {/* Name + position */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-[10px] font-bold text-primary shrink-0">
            {p.athlete_name?.charAt(0)}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{p.athlete_name}</p>
            <p className="text-[10px] text-muted-foreground capitalize">{p.position || p.position_group || '—'}</p>
          </div>
        </div>

        {!p.evaluated && p.included && (
          <span className="text-[9px] px-1.5 py-0.5 bg-primary/10 border border-primary/20 rounded-sm text-primary">NEW</span>
        )}
      </div>

      {p.included && (
        <div className="ml-9 space-y-3">
          {/* Row 1: Rating + Goals/Assists/Saves/Errors */}
          <div className="flex flex-wrap gap-3">
            <EditField label="Rating" small>
              <select value={p.rating || 'average'} onChange={e => onChange(index, 'rating', e.target.value)}
                className="field-sm" data-testid={`edit-rating-${index}`}
              >
                <option value="">— Select —</option>
                {ratingOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </EditField>
            <EditField label="Goals" small>
              <input type="number" min="0" max="20" value={p.goals || 0}
                onChange={e => onChange(index, 'goals', parseInt(e.target.value) || 0)}
                className="field-sm w-16" data-testid={`edit-goals-${index}`} />
            </EditField>
            <EditField label="Assists" small>
              <input type="number" min="0" max="20" value={p.assists || 0}
                onChange={e => onChange(index, 'assists', parseInt(e.target.value) || 0)}
                className="field-sm w-16" data-testid={`edit-assists-${index}`} />
            </EditField>
            {isGK && (
              <>
                <EditField label="Saves" small>
                  <input type="number" min="0" max="30" value={p.saves || 0}
                    onChange={e => onChange(index, 'saves', parseInt(e.target.value) || 0)}
                    className="field-sm w-16" data-testid={`edit-saves-${index}`} />
                </EditField>
                <EditField label="Errors" small>
                  <input type="number" min="0" max="20" value={p.errors || 0}
                    onChange={e => onChange(index, 'errors', parseInt(e.target.value) || 0)}
                    className="field-sm w-16" data-testid={`edit-errors-${index}`} />
                </EditField>
              </>
            )}
            <EditField label="Flag" small>
              <select value={p.flag || 'no_issue'} onChange={e => onChange(index, 'flag', e.target.value)}
                className="field-sm" data-testid={`edit-flag-${index}`}
              >
                {flagOptions.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </EditField>
          </div>

          {/* Row 2: Contributions */}
          <div>
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Contributions</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {contributionOptions.map(c => (
                <button key={c} type="button"
                  onClick={() => onToggleContrib(index, c)}
                  className={`text-[9px] px-2 py-0.5 rounded-sm border transition-colors ${
                    (p.contributions || []).includes(c)
                      ? 'bg-primary/15 text-primary border-primary/30'
                      : 'bg-zinc-800 text-zinc-500 border-border/30 hover:border-primary/30'
                  }`}
                  data-testid={`contrib-${index}-${c}`}
                >{c.replace(/_/g, ' ')}</button>
              ))}
            </div>
          </div>

          {/* Row 3: Note */}
          <EditField label="Notes" small>
            <input type="text" value={p.note || ''} placeholder="Optional notes..."
              onChange={e => onChange(index, 'note', e.target.value)}
              className="field-sm w-full" data-testid={`edit-note-${index}`} />
          </EditField>
        </div>
      )}
    </div>
  );
};


/* ============ Helpers ============ */
const EditField = ({ label, children, small }) => (
  <div className={small ? '' : ''}>
    <label className={`${small ? 'text-[9px]' : 'text-[10px]'} uppercase tracking-widest text-muted-foreground block mb-1`}>{label}</label>
    {children}
  </div>
);


const AISummaryBlock = ({ summary }) => {
  const s = summary || {};
  const renderVal = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'string') return val;
    if (typeof val === 'number') return String(val);
    if (Array.isArray(val)) {
      if (!val.length) return null;
      if (typeof val[0] === 'string') return val.join(', ');
      return val.map((item, i) => (
        <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
          {item.name && <span className="font-medium text-white/80">{item.name}</span>}
          {item.rating && <span className="text-xs text-muted-foreground capitalize">({item.rating})</span>}
          {item.goals > 0 && <span className="text-xs text-primary">{item.goals}G</span>}
          {item.assists > 0 && <span className="text-xs text-emerald-400">{item.assists}A</span>}
        </div>
      ));
    }
    if (typeof val === 'object') {
      return Object.entries(val).filter(([, v]) => v != null).map(([k, v]) => (
        <div key={k} className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}:</span>
          <span className="text-white/80">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
        </div>
      ));
    }
    return String(val);
  };

  return (
    <div className="space-y-3">
      {s.match_result && <p className="text-sm font-medium">{s.match_result}</p>}
      {s.team_performance && typeof s.team_performance === 'string' && (
        <p className="text-sm text-muted-foreground capitalize">Team Performance: <span className="text-primary">{s.team_performance}</span></p>
      )}
      {s.comparison && typeof s.comparison === 'object' && (
        <div className="flex flex-wrap gap-3 p-3 bg-zinc-900/50 rounded-sm border border-border/20">
          {s.comparison.score_trend && (
            <div className="text-center"><p className="text-[10px] text-muted-foreground uppercase">Trend</p>
              <p className={`text-sm font-medium capitalize ${s.comparison.score_trend === 'improving' ? 'text-emerald-400' : s.comparison.score_trend === 'declining' ? 'text-red-400' : 'text-amber-400'}`}>{s.comparison.score_trend}</p></div>
          )}
          {s.comparison.total_matches_analyzed > 0 && (
            <div className="text-center"><p className="text-[10px] text-muted-foreground uppercase">Analyzed</p><p className="text-sm font-medium">{s.comparison.total_matches_analyzed} matches</p></div>
          )}
          {s.comparison.win_rate >= 0 && (
            <div className="text-center"><p className="text-[10px] text-muted-foreground uppercase">Win Rate</p><p className="text-sm font-medium text-primary">{s.comparison.win_rate}%</p></div>
          )}
        </div>
      )}
      {s.standout_players?.length > 0 && (
        <div className="p-3 bg-emerald-500/5 border border-emerald-500/10 rounded-sm">
          <p className="text-[10px] uppercase text-emerald-400 mb-2">Standout Players</p><div className="space-y-1">{renderVal(s.standout_players)}</div></div>
      )}
      {s.scorers?.length > 0 && (
        <div className="p-3 bg-primary/5 border border-primary/10 rounded-sm">
          <p className="text-[10px] uppercase text-primary mb-2">Scorers</p><div className="space-y-1">{renderVal(s.scorers)}</div></div>
      )}
      {s.concerns?.length > 0 && (
        <div className="p-3 bg-red-500/5 border border-red-500/10 rounded-sm">
          <p className="text-[10px] uppercase text-red-400 mb-2">Concerns</p><div className="space-y-1">{renderVal(s.concerns)}</div></div>
      )}
      {s.insights && (
        <div className="p-3 bg-primary/5 border border-primary/10 rounded-sm">
          <p className="text-[10px] uppercase text-primary mb-1">Insights</p><p className="text-sm text-muted-foreground">{typeof s.insights === 'string' ? s.insights : JSON.stringify(s.insights)}</p></div>
      )}
    </div>
  );
};

export default MatchReportDetail;
